import { lang } from '@umu/ukit-lang';
import './index.scss';
import { Iconfont } from '@umu/umu-design';
import React, { useCallback } from 'react';
import { PaginationEllipsisPagesPicker } from './ellipsisPagesPicker';
import { PaginationPageSizeSelect } from './pageSizeSelect';

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 50, 100, 200];

interface IProps {
    className?: string;
    currentPage: number;
    maxPage: number;
    forceShow?: boolean;
    pageOffset?: number;
    /** 小方格页码样式（历史命名，与「每页条数」无关） */
    showSizeChanger?: boolean;
    onPageChange?(page: number): void;

    /** 左侧总条数区域，默认不展示 */
    showTotal?: boolean;
    totalCount?: number;
    /** 自定义总条数文案；不传则「共 n 条」 */
    formatTotal?: (total: number) => React.ReactNode;

    /** 「n 条/页」下拉，默认不展示 */
    showPageSizeSelector?: boolean;
    pageSize?: number;
    pageSizeOptions?: number[];
    onPageSizeChange?: (size: number) => void;

    /** 是否展示首页、尾页；默认 true 保持历史行为 */
    showFirstLastPage?: boolean;

    /**
     * 「…」hover/点击展开被省略的页码并跳转；为 false 时保持原逻辑（点击 … 向前/后跳 10 页）
     * @default true
     */
    ellipsisPagePicker?: boolean;
}

const PAGE_OFFSET = 4;

export const PaginationV2 = ({
    currentPage,
    maxPage,
    forceShow,
    className = '',
    showSizeChanger,
    pageOffset = PAGE_OFFSET,
    showTotal = false,
    totalCount = 0,
    formatTotal,
    showPageSizeSelector = false,
    pageSize,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    onPageSizeChange,
    showFirstLastPage = true,
    ellipsisPagePicker = true,
    onPageChange,
}: IProps) => {
    currentPage = Number(currentPage);
    maxPage = Number(maxPage);
    let start = currentPage - pageOffset;
    let end = currentPage + pageOffset;

    if (start < 1) {
        const padding = Math.abs(1 - start);
        start += padding;
        end += padding;
    } else if (end > maxPage) {
        const padding = Math.abs(end - maxPage);
        start -= padding;
        end -= padding;
    }

    start = Math.max(1, start);
    end = Math.min(end, maxPage);

    const list = Array.from(Array(end - start + 1).keys()).map((_) => _ + start);

    const showHead = currentPage === 1;
    const showTail = currentPage === maxPage;
    const showJumpPrev = list.indexOf(1) === -1;
    const showJumpNext = list.indexOf(maxPage) === -1;
    const showJumpPerPage = maxPage > 5;

    const onClick = useCallback(
        (page: number) => {
            if (page === currentPage) {
                return;
            }
            onPageChange?.(page);
        },
        [currentPage, onPageChange],
    );

    const onJump = useCallback(
        (offset: number) => {
            let page = currentPage + offset;
            if (page < 1) {
                page = 1;
            } else if (page > maxPage) {
                page = maxPage;
            }
            onClick(page);
        },
        [currentPage, maxPage, onClick],
    );

    const hasLeftExtras = !!(showTotal || showPageSizeSelector);
    const effectiveForceShow = !!forceShow || hasLeftExtras;

    if (!effectiveForceShow && list.length <= 1) {
        return null;
    }

    const totalNode =
        showTotal &&
        (formatTotal ? (
            formatTotal(totalCount)
        ) : (
            <span className="umu-pagination-v2-total">{lang('共{$0}条', totalCount)}</span>
        ));

    const sizeOptions = pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;

    const leftOverflowPages: number[] = [];
    if (showJumpPrev) {
        for (let p = 1; p < start; p++) {
            if (showFirstLastPage && !showHead && p === 1) {
                continue;
            }
            leftOverflowPages.push(p);
        }
    }

    const rightOverflowPages: number[] = [];
    if (showJumpNext) {
        const rightEnd = showFirstLastPage && !showTail ? maxPage - 1 : maxPage;
        for (let p = end + 1; p <= rightEnd; p++) {
            rightOverflowPages.push(p);
        }
    }

    return (
        <div
            className={`umu-pagination-v2 umu-pagination-bar ${
                showSizeChanger ? 'umu-pagination-bar-box' : 'umu-pagination-bar-normal'
            } ${hasLeftExtras ? 'umu-pagination-v2--with-left' : ''} ${className}`}
        >
            {hasLeftExtras ? (
                <div className="umu-pagination-v2-left">
                    {totalNode}
                    {showPageSizeSelector && onPageSizeChange && pageSize != null ? (
                        <div className="umu-pagination-v2-page-size">
                            <PaginationPageSizeSelect
                                selected={pageSize}
                                options={sizeOptions}
                                onSelect={onPageSizeChange}
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}
            <ul className="page-list umu-pagination-v2-page-list">
                {showFirstLastPage && !showHead ? (
                    <li className="page-item" onClick={() => onClick(1)}>
                        {lang('首页_First')}
                    </li>
                ) : null}
                {showJumpPerPage && !showHead ? (
                    <li className="page-item page-item-prev" onClick={() => onJump(-1)}>
                        <Iconfont
                            type="SmallBack"
                            className={showSizeChanger ? 'small-more-icon gray' : 'small-more-icon blue'}
                        />
                    </li>
                ) : null}
                {showJumpPrev ? (
                    ellipsisPagePicker ? (
                        <PaginationEllipsisPagesPicker
                            pages={leftOverflowPages}
                            currentPage={currentPage}
                            onPageSelect={onClick}
                            onFallbackJump={() => onJump(-10)}
                        />
                    ) : (
                        <li className="page-item" onClick={() => onJump(-10)}>
                            ...
                        </li>
                    )
                ) : null}
                {list.map((page) => {
                    return (
                        <li
                            key={page}
                            className={'page-item' + (page === currentPage ? ' current' : '')}
                            onClick={() => onClick(page)}
                        >
                            {page}
                        </li>
                    );
                })}
                {showJumpNext ? (
                    ellipsisPagePicker ? (
                        <PaginationEllipsisPagesPicker
                            pages={rightOverflowPages}
                            currentPage={currentPage}
                            onPageSelect={onClick}
                            onFallbackJump={() => onJump(10)}
                        />
                    ) : (
                        <li className="page-item" onClick={() => onJump(10)}>
                            ...
                        </li>
                    )
                ) : null}
                {forceShow || (showJumpPerPage && !showTail) ? (
                    <li className="page-item page-item-next" onClick={() => onJump(1)}>
                        <Iconfont
                            type="SmallMore"
                            className={showSizeChanger ? 'small-more-icon gray' : 'small-more-icon blue'}
                        />
                    </li>
                ) : null}
                {showFirstLastPage && (!showTail || !!forceShow) ? (
                    <li className="page-item" onClick={() => onClick(maxPage)}>
                        {lang('尾页_Last')}
                    </li>
                ) : null}
            </ul>
        </div>
    );
};
